import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect } from "react";

gsap.registerPlugin(ScrollTrigger);

interface StaggerGroup {
  itemsSelector: string;
  triggerSelector: string;
  offsetY?: number;
  duration?: number;
  stagger?: number;
}

const STAGGER_GROUPS: StaggerGroup[] = [
  {
    itemsSelector: ".teamGrid .teamCard",
    triggerSelector: ".teamGrid",
    stagger: 0.03,
    duration: 0.24,
  },
  {
    itemsSelector: ".osGrid .openSourceCard",
    triggerSelector: ".osGrid",
    stagger: 0.03,
    duration: 0.24,
  },
  {
    itemsSelector: ".projectGrid .projectCard",
    triggerSelector: ".projectGrid",
    stagger: 0.025,
    duration: 0.22,
  },
  {
    itemsSelector: ".contactLinks .contactLink",
    triggerSelector: ".contactLinks",
    offsetY: 8,
    stagger: 0.02,
    duration: 0.2,
  },
];

const useScrollAnimation = () => {
  useEffect(() => {
    const fadeItems = gsap.utils.toArray<HTMLElement>(".fi");
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      fadeItems.forEach((element) => element.classList.add("vis"));
      return;
    }

    const context = gsap.context(() => {
      fadeItems.forEach((element) => {
        gsap.fromTo(
          element,
          { autoAlpha: 0, y: 10 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.2,
            ease: "none",
            onStart: () => element.classList.add("vis"),
            scrollTrigger: {
              trigger: element,
              start: "top 95%",
              once: true,
            },
          },
        );
      });

      STAGGER_GROUPS.forEach(
        ({
          itemsSelector,
          triggerSelector,
          offsetY = 10,
          duration = 0.22,
          stagger = 0.03,
        }) => {
          const items = gsap.utils.toArray<HTMLElement>(itemsSelector);
          if (!items.length) return;

          gsap.fromTo(
            items,
            { autoAlpha: 0, y: offsetY },
            {
              autoAlpha: 1,
              y: 0,
              duration,
              stagger,
              ease: "none",
              scrollTrigger: {
                trigger: triggerSelector,
                start: "top 92%",
                once: true,
              },
            },
          );
        },
      );

      const aboutBoxes = gsap.utils.toArray<HTMLElement>(".aboutVis .abox");
      if (aboutBoxes.length === 3) {
        gsap.to(aboutBoxes[0], {
          yPercent: -6,
          ease: "none",
          scrollTrigger: {
            trigger: ".about",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });

        gsap.to(aboutBoxes[1], {
          yPercent: 8,
          ease: "none",
          scrollTrigger: {
            trigger: ".about",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });

        gsap.to(aboutBoxes[2], {
          yPercent: -10,
          ease: "none",
          scrollTrigger: {
            trigger: ".about",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    });

    return () => context.revert();
  }, []);
};

export default useScrollAnimation;
